import { useCallback, useEffect, useState } from "react";

// Where the note assistant lives at each width.
// - panel: a column to the right of the note (desktop, from 1024px).
// - drawer: a right-hand drawer over the note (tablet, 768–1023px).
// - sheet: a bottom sheet (phone, under 768px).
export type AssistantPlacement = "panel" | "drawer" | "sheet";

export const WORKSPACE_BREAKPOINTS = {
  drawer: 768,
  panel: 1024,
} as const;

export const assistantPlacementFor = (width: number): AssistantPlacement => {
  if (width >= WORKSPACE_BREAKPOINTS.panel) return "panel";
  if (width >= WORKSPACE_BREAKPOINTS.drawer) return "drawer";
  return "sheet";
};

const currentPlacement = (): AssistantPlacement =>
  typeof window === "undefined"
    ? "panel"
    : assistantPlacementFor(window.innerWidth);

export const useAssistantPlacement = (): AssistantPlacement => {
  const [placement, setPlacement] =
    useState<AssistantPlacement>(currentPlacement);

  useEffect(() => {
    const queries = Object.values(WORKSPACE_BREAKPOINTS).map((width) =>
      window.matchMedia(`(min-width: ${width}px)`),
    );
    const update = (): void => {
      setPlacement(currentPlacement());
    };
    for (const query of queries) query.addEventListener("change", update);
    update();
    return (): void => {
      for (const query of queries) query.removeEventListener("change", update);
    };
  }, []);

  return placement;
};

// Desktop keeps the provider's last choice of an open or collapsed panel.
// Storage can be unavailable (private mode); the panel then starts open.
export const ASSISTANT_PANEL_STORAGE_KEY = "rosc.visitWorkspace.assistantPanel";

const readPanelPreference = (): boolean => {
  try {
    return (
      window.localStorage.getItem(ASSISTANT_PANEL_STORAGE_KEY) !== "closed"
    );
  } catch {
    return true;
  }
};

const writePanelPreference = (open: boolean): void => {
  try {
    window.localStorage.setItem(
      ASSISTANT_PANEL_STORAGE_KEY,
      open ? "open" : "closed",
    );
  } catch {
    // Preference is a convenience only.
  }
};

export interface AssistantSurface {
  placement: AssistantPlacement;
  // Panel: expanded column. Drawer and sheet: shown over the page.
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

// The desktop panel starts open (remembered); the drawer and sheet start
// closed and open on demand, so the note gets the whole screen on touch
// devices.
export const useAssistantSurface = (): AssistantSurface => {
  const placement = useAssistantPlacement();
  const [panelOpen, setPanelOpen] = useState(readPanelPreference);
  const [overlayOpen, setOverlayOpen] = useState(false);

  useEffect(() => {
    if (placement === "panel") setOverlayOpen(false);
  }, [placement]);

  const open = placement === "panel" ? panelOpen : overlayOpen;
  const setOpen = useCallback(
    (next: boolean): void => {
      if (placement === "panel") {
        setPanelOpen(next);
        writePanelPreference(next);
      } else {
        setOverlayOpen(next);
      }
    },
    [placement],
  );
  const toggle = useCallback((): void => {
    setOpen(!open);
  }, [open, setOpen]);

  return { placement, open, setOpen, toggle };
};

// Phones (under 640px): the compact note toolbar (Copy and Edit, the rest in
// a "…" menu) and the transcript docked at the bottom of the screen, so the
// note starts higher and keeps the screen.
export const PHONE_MAX_WIDTH = 639;

const matchesQuery = (query: string): boolean =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(query).matches
    : false;

export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => matchesQuery(query));
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const list = window.matchMedia(query);
    const update = (): void => {
      setMatches(list.matches);
    };
    list.addEventListener("change", update);
    update();
    return (): void => {
      list.removeEventListener("change", update);
    };
  }, [query]);
  return matches;
};

export const usePhoneLayout = (): boolean =>
  useMediaQuery(`(max-width: ${PHONE_MAX_WIDTH}px)`);
